import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { forwardRef, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { isValidPhoneNumber } from "libphonenumber-js";
import { getCountryCallingCode } from "libphonenumber-js";
import { cn } from "@/lib/utils";
import { ChevronDown, Check, Search, Globe } from "lucide-react";

const FlagIcon = ({ country }) => {
  if (!country) return <Globe className="h-4 w-4 text-muted-foreground" />;
  const Flag = flags[country];
  if (!Flag) return <Globe className="h-4 w-4 text-muted-foreground" />;
  return <Flag title={country} className="w-5 h-4 object-cover rounded-sm" />;
};

const CountrySelect = ({ value, onChange, options = [], disabled, tabIndex }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const list = options.filter((o) => !o.divider);
    if (!q) return list;
    return list.filter((o) => {
      if (o.label?.toLowerCase().includes(q)) return true;
      if (o.value) {
        try {
          const code = getCountryCallingCode(o.value).toString();
          if (code.includes(q.replace("+", ""))) return true;
        } catch { return false; }
      }
      return false;
    });
  }, [options, search]);

  const handleSelect = useCallback(
    (optValue) => {
      onChange(optValue || undefined);
      setSearch("");
      setOpen(false);
    },
    [onChange]
  );

  return (
    <div ref={dropdownRef} className="relative h-full">
      <button
        type="button"
        disabled={disabled}
        tabIndex={tabIndex}
        onClick={() => setOpen((o) => !o)}
        className="h-full flex items-center gap-0.5 px-2.5 border-r border-input bg-transparent hover:bg-muted/60 transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none shrink-0"
      >
        <FlagIcon country={value} />
        <ChevronDown className="h-3 w-3 opacity-50" />
      </button>

      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 w-[280px] rounded-md border border-input bg-popover text-popover-foreground shadow-md">
          <div className="flex items-center border-b border-input px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50 mr-2" />
            <input
              autoFocus
              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              placeholder="Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="max-h-[250px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">No country found.</div>
            ) : (
              filtered.map((opt) => {
                let callingCode = null;
                if (opt.value) {
                  try {
                    callingCode = getCountryCallingCode(opt.value);
                  } catch { callingCode = null; }
                }
                return (
                  <div
                    key={opt.value ?? "__intl__"}
                    onClick={() => handleSelect(opt.value)}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                  >
                    <span className="w-6 flex items-center justify-center">
                      <FlagIcon country={opt.value} />
                    </span>
                    <span className="flex-1 truncate text-sm">{opt.label}</span>
                    {callingCode && (
                      <span className="text-xs text-muted-foreground">+{callingCode}</span>
                    )}
                    {opt.value === value && (
                      <Check className="h-3.5 w-3.5 text-primary ml-0.5 shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomPhoneInput = forwardRef(({ className, ...props }, ref) => (
  <input
    ref={ref}
    {...props}
    className={cn(
      "flex-1 h-full bg-transparent px-3 py-2 text-sm outline-none min-w-0",
      "placeholder:text-muted-foreground",
      "disabled:cursor-not-allowed disabled:opacity-50",
      className
    )}
  />
));
CustomPhoneInput.displayName = "CustomPhoneInput";

const PhoneInputComponent = forwardRef((props, ref) => {
  const {
    value = "",
    onChange,
    label,
    placeholder = "Enter phone number",
    defaultCountry = "SA",
    className,
    error,
    required,
    onValidationChange,
    ...rest
  } = props;

  const [internalError, setInternalError] = useState("");
  const containerRef = useRef(null);
  const isValidRef = useRef(false);

  const isValid = useMemo(() => {
    if (!value) return false;
    try {
      return isValidPhoneNumber(value);
    } catch {
      return false;
    }
  }, [value]);

  // Keep ref in sync for use inside event listener
  useEffect(() => {
    isValidRef.current = isValid;
  }, [isValid]);

  // Block extra digit keystrokes once number is valid
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const input =
      container.querySelector("input[type='tel']") || container.querySelector("input");
    if (!input) return;
    const handleKeyDown = (e) => {
      if (isValidRef.current && /^\d$/.test(e.key)) e.preventDefault();
    };
    input.addEventListener("keydown", handleKeyDown);
    return () => input.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handlePhoneChange = useCallback(
    (phoneValue) => {
      onChange?.(phoneValue ?? "");
      if (!phoneValue) {
        setInternalError("");
        onValidationChange?.(true, phoneValue);
        return;
      }
      try {
        const valid = isValidPhoneNumber(phoneValue);
        setInternalError(valid ? "" : "Please enter a valid phone number");
        onValidationChange?.(valid, phoneValue);
      } catch {
        setInternalError("Please enter a valid phone number");
        onValidationChange?.(false, phoneValue);
      }
    },
    [onChange, onValidationChange]
  );

  const displayError = error || internalError;

  return (
    <div ref={containerRef} className={cn("w-full", className)}>
      {label && (
        <label className="mb-1.5 block text-sm font-medium">
          {label} {required && <span className="text-destructive">*</span>}
        </label>
      )}

      <div
        className={cn(
          "flex h-10 rounded-md border border-input bg-background",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          displayError && "border-destructive focus-within:ring-destructive/50"
        )}
      >
        <PhoneInput
          ref={ref}
          international
          defaultCountry={defaultCountry}
          value={value}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          limitMaxLength
          inputComponent={CustomPhoneInput}
          countrySelectComponent={CountrySelect}
          className="flex w-full h-full [&_.PhoneInputCountry]:m-0 [&_.PhoneInputCountry]:p-0 [&_.PhoneInputCountry]:h-full [&_.PhoneInputCountry]:flex [&_.PhoneInputCountry]:items-stretch"
          {...rest}
        />
      </div>

      {displayError && <p className="mt-1.5 text-xs text-destructive">{displayError}</p>}

      {!displayError && isValid && (
        <p className="mt-1.5 text-xs text-green-600">✓ Valid phone number</p>
      )}
    </div>
  );
});

PhoneInputComponent.displayName = "PhoneInputComponent";

export default PhoneInputComponent;
